import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function FtpImportPanel({ events, onImported }) {
  const { user } = useAuth();
  const defaultCur = (user?.default_currency || "gbp").toLowerCase();
  const [ftpConfigured, setFtpConfigured] = useState(false);
  const [defaultPath, setDefaultPath] = useState("/");
  const [path, setPath] = useState("/");
  const [eventId, setEventId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(defaultCur);
  const [titlePrefix, setTitlePrefix] = useState("");
  const [files, setFiles] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [], skipped: 0 });

  const selectedEvent = events.find((e) => e.id === eventId);
  const groups = selectedEvent?.groups || [];

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/admin/ftp/settings");
      setFtpConfigured(!!data.configured);
      if (data.default_path) {
        setDefaultPath(data.default_path);
        if (path === "/") setPath(data.default_path);
      }
    } catch (e) {
      setFtpConfigured(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { loadConfig(); }, []);

  const browse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ftp/list", { path });
      setFiles(data.files || []);
      const auto = new Set();
      (data.files || []).forEach((f) => {
        if (!f.is_dir && !f.already_imported) {
          const ext = (f.name.split(".").pop() || "").toLowerCase();
          if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) auto.add(f.name);
        }
      });
      setSelected(auto);
    } catch (e) {
      toast.error(formatApiError(e));
      setFiles([]);
    } finally {
      setBusy(false);
    }
  };

  const toggleAll = (checked) => {
    if (!files) return;
    if (checked) {
      const all = new Set();
      files.forEach((f) => {
        if (!f.is_dir) all.add(f.name);
      });
      setSelected(all);
    } else {
      setSelected(new Set());
    }
  };

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    setSelected(next);
  };

  const runImport = async () => {
    if (!eventId || !groupId) { toast.error("Pick event and class first."); return; }
    if (price === "" || Number(price) < 0) { toast.error("Set a price (0 or more)."); return; }
    const list = Array.from(selected);
    if (list.length === 0) { toast.error("No files selected."); return; }

    const BATCH = 6;
    let imported = 0;
    let skipped = 0;
    const errors = [];
    setBusy(true);
    setProgress({ done: 0, total: list.length, errors: [], skipped: 0 });

    try {
      for (let i = 0; i < list.length; i += BATCH) {
        const chunk = list.slice(i, i + BATCH);
        try {
          const { data } = await api.post("/admin/ftp/import", {
            path,
            filenames: chunk,
            event_id: eventId,
            group_id: groupId,
            price: Number(price),
            currency,
            title_prefix: titlePrefix,
          }, { timeout: 5 * 60 * 1000 });
          imported += data.count || 0;
          skipped += (data.skipped || []).length;
          if (data.errors && data.errors.length) errors.push(...data.errors);
        } catch (batchErr) {
          chunk.forEach((n) => errors.push({ filename: n, error: formatApiError(batchErr) }));
        }
        setProgress({ done: imported, total: list.length, errors, skipped });
      }
      if (errors.length) {
        toast.warning(`${imported} imported · ${skipped} skipped · ${errors.length} failed`);
      } else {
        toast.success(`Imported ${imported} photo${imported === 1 ? "" : "s"}${skipped ? `, ${skipped} already-present skipped` : ""}`);
      }
      await browse();
      onImported();
    } finally {
      setBusy(false);
    }
  };

  if (!ftpConfigured) {
    return (
      <div className="border border-white/10 p-10 text-center space-y-4" data-testid="ftp-not-configured">
        <p className="font-serif text-2xl text-white/80">FTP not configured</p>
        <p className="text-white/50 text-sm">Add your FTP host, username and password in the Settings tab first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="ftp-import-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Folder path on FTP</label>
            <div className="flex gap-3">
              <input
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder={defaultPath}
                className="input-minimal flex-1"
                data-testid="ftp-path-input"
              />
              <button
                onClick={browse}
                disabled={busy}
                className="bg-white text-black px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
                data-testid="ftp-browse"
              >
                {busy ? "…" : "Browse"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Event</label>
              <select
                value={eventId}
                onChange={(e) => { setEventId(e.target.value); setGroupId(""); }}
                className="input-minimal bg-black"
                data-testid="ftp-event"
              >
                <option value="">— select event —</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.date} · {ev.track}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Class</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={!eventId}
                className="input-minimal bg-black disabled:opacity-50"
                data-testid="ftp-group"
              >
                <option value="">— select class —</option>
                {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Price (per photo)</label>
              <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="input-minimal" data-testid="ftp-price" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="input-minimal bg-black" data-testid="ftp-currency">
                <option value="gbp">GBP</option><option value="usd">USD</option><option value="eur">EUR</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Title prefix (optional)</label>
            <input value={titlePrefix} onChange={(e) => setTitlePrefix(e.target.value)} placeholder="defaults to filename" className="input-minimal" data-testid="ftp-prefix" />
          </div>

          <button
            onClick={runImport}
            disabled={busy || !files || selected.size === 0}
            className="bg-white text-black px-10 py-4 text-xs uppercase tracking-[0.3em] font-medium hover:bg-white/90 transition disabled:opacity-50"
            data-testid="ftp-import-button"
          >
            {busy && progress.total > 0
              ? `Importing ${progress.done}/${progress.total}…`
              : selected.size === 0
              ? "Import selected"
              : `Import ${selected.size} file${selected.size === 1 ? "" : "s"}`}
          </button>

          {busy && progress.total > 0 && (
            <div className="space-y-2" data-testid="ftp-progress">
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-white transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
              <p className="text-xs text-white/60">
                Batches of 6 · {progress.done}/{progress.total} imported
                {progress.skipped > 0 && <span className="text-white/40 ml-2">· {progress.skipped} skipped</span>}
                {progress.errors.length > 0 && <span className="text-red-400 ml-2">· {progress.errors.length} failed</span>}
              </p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-[0.3em] text-white/40">
              {files === null ? "Browse the FTP folder" : `${files.length} entr${files.length === 1 ? "y" : "ies"} · ${selected.size} selected`}
            </label>
            {files && files.length > 0 && (
              <div className="flex gap-3">
                <button onClick={() => toggleAll(true)} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white" data-testid="ftp-select-all">Select all</button>
                <button onClick={() => toggleAll(false)} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white" data-testid="ftp-select-none">None</button>
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-[#0a0a0a] max-h-[480px] overflow-auto" data-testid="ftp-file-list">
            {files === null ? (
              <p className="text-white/40 text-sm p-6 text-center">Press Browse to list files in the folder.</p>
            ) : files.length === 0 ? (
              <p className="text-white/40 text-sm p-6 text-center">No files in this folder.</p>
            ) : (
              <ul>
                {files.map((f) => (
                  <li
                    key={f.name}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-white/5 ${f.already_imported ? "opacity-50" : ""}`}
                    data-testid={`ftp-file-${f.name}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(f.name)}
                      onChange={() => toggle(f.name)}
                      disabled={f.is_dir}
                      className="accent-white"
                    />
                    <span className="flex-1 text-sm text-white/80 truncate">
                      {f.is_dir ? "📁 " : ""}{f.name}
                    </span>
                    {!f.is_dir && (
                      <span className="text-xs text-white/40">
                        {(f.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    )}
                    {f.already_imported && (
                      <span className="text-xs uppercase tracking-[0.2em] text-white/40">Imported</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
